import { map, Observable, OperatorFunction, tap } from 'rxjs';
import { ChangeTyped, Dto, SearchResult } from '@damofire/data-commons';
import { where } from '@firebase/firestore';
import { FirebaseObservableService, FirebaseObservableServiceFactory } from '@damofire/data-angular-commons';
import { TypedDesQuery } from './des.dto';

export class FirebaseWatsonObservableService<T extends Dto> {
  private readonly service: FirebaseObservableService<T>;

  constructor(
    firebaseObservableServiceFactory: FirebaseObservableServiceFactory,
    protected readonly collectionName: string,
  ) {
    this.service = firebaseObservableServiceFactory.createService(collectionName);
  }

  private observeByWarehouse(id: string): Observable<T[]> {
    return this.service.observeCollection([where('division._id', '==', id)]);
  }

  private observeByWarehouseWithSection(id: string, section: string | number): Observable<T[]> {
    if (!section || section === -1) {
      return this.service.observeCollectionIsolated([where('division._id', '==', id)]);
    }
    return this.service.observeCollectionIsolated([where('division._id', '==', id), where('asset.section', '==', section)]);
  }

  observeByWarehouseWithSectionPiped(id: string, section: string, pipeFunction: OperatorFunction<T[], T[]>): Observable<ChangeTyped<T>[]> {
    let previousSearch: ChangeTyped<T>[] = [];
    return this.observeByWarehouseWithSection(id, section).pipe(
      pipeFunction,
      map((fromFirestore: T[]) => FirebaseObservableService.mergeChanges(fromFirestore, previousSearch)),
      tap((changes: ChangeTyped<T>[]) => (previousSearch = changes.filter((c) => c.changeType !== 'removed'))),
    );
  }
  observeByWarehousePiped(id: string, pipeFunction: OperatorFunction<T[], T[]>): Observable<ChangeTyped<T>[]> {
    let previousSearch: ChangeTyped<T>[] = [];
    return this.observeByWarehouse(id).pipe(
      pipeFunction,
      map((fromFirestore: T[]) => FirebaseObservableService.mergeChanges(fromFirestore, previousSearch)),
      tap((changes: ChangeTyped<T>[]) => (previousSearch = changes.filter((c) => c.changeType !== 'removed'))),
    );
  }

  observeDoc(id: string): Observable<ChangeTyped<T>> {
    return this.service.observeDoc(id);
  }

  search(id: string, sQuery: TypedDesQuery, searchFields: string[]): Observable<SearchResult<T>> {
    return this.observeByWarehouse(id).pipe(map((data) => this.service.search<T>(data, sQuery, searchFields)));
  }
}
